"use client";

import { lazy } from "react";
import type { ToolUIMeta, ToolUIRegistry } from "../../tool-renderer";

/**
 * Tier 1 (static) views for the admin-only `devtools` capability. Every key is
 * a bare snake_case tool name that IS part of the compile-time `AllToolNames`
 * union (progressive-disclosure tools are still in the union), so all 16 must
 * be present or the exhaustiveness `satisfies` in `apps/web` fails.
 *
 * Written as explicit literal keys (not generated) on purpose: a string-indexed
 * object would collapse the static registry's key type to `string` and silently
 * defeat the `Record<AllToolNames, ToolUIMeta>` exhaustiveness guard for every
 * capability. Each view is lazy per `bundle-dynamic-imports`.
 */
const views = {
  feedback_search: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ FeedbackSearchToolView }) => ({
        default: FeedbackSearchToolView,
      }))
    ),
  },
  feedback_get: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ FeedbackGetToolView }) => ({
        default: FeedbackGetToolView,
      }))
    ),
  },
  feedback_get_trace: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ FeedbackGetTraceToolView }) => ({
        default: FeedbackGetTraceToolView,
      }))
    ),
  },
  feedback_insights: {
    kind: "step",
    category: "metrics",
    view: lazy(() =>
      import("./views").then(({ FeedbackInsightsToolView }) => ({
        default: FeedbackInsightsToolView,
      }))
    ),
  },
  feedback_triage: {
    kind: "step",
    category: "message",
    view: lazy(() =>
      import("./views").then(({ FeedbackTriageToolView }) => ({
        default: FeedbackTriageToolView,
      }))
    ),
  },
  upload_summary: {
    kind: "step",
    category: "metrics",
    view: lazy(() =>
      import("./views").then(({ UploadSummaryToolView }) => ({
        default: UploadSummaryToolView,
      }))
    ),
  },
  upload_list: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ UploadListToolView }) => ({
        default: UploadListToolView,
      }))
    ),
  },
  metrics_summary: {
    kind: "step",
    category: "metrics",
    view: lazy(() =>
      import("./views").then(({ MetricsSummaryToolView }) => ({
        default: MetricsSummaryToolView,
      }))
    ),
  },
  metrics_timeseries: {
    kind: "step",
    category: "metrics",
    view: lazy(() =>
      import("./views").then(({ MetricsTimeseriesToolView }) => ({
        default: MetricsTimeseriesToolView,
      }))
    ),
  },
  metrics_breakdown: {
    kind: "step",
    category: "metrics",
    view: lazy(() =>
      import("./views").then(({ MetricsBreakdownToolView }) => ({
        default: MetricsBreakdownToolView,
      }))
    ),
  },
  metrics_tools: {
    kind: "step",
    category: "metrics",
    view: lazy(() =>
      import("./views").then(({ MetricsToolsToolView }) => ({
        default: MetricsToolsToolView,
      }))
    ),
  },
  metrics_recent_streams: {
    kind: "step",
    category: "metrics",
    view: lazy(() =>
      import("./views").then(({ MetricsRecentStreamsToolView }) => ({
        default: MetricsRecentStreamsToolView,
      }))
    ),
  },
  metrics_by_run: {
    kind: "step",
    category: "metrics",
    view: lazy(() =>
      import("./views").then(({ MetricsByRunToolView }) => ({
        default: MetricsByRunToolView,
      }))
    ),
  },
  logs_query_aca: {
    kind: "step",
    category: "metrics",
    view: lazy(() =>
      import("./views").then(({ LogsQueryAcaToolView }) => ({
        default: LogsQueryAcaToolView,
      }))
    ),
  },
  appinsights_query: {
    kind: "step",
    category: "metrics",
    view: lazy(() =>
      import("./views").then(({ AppinsightsQueryToolView }) => ({
        default: AppinsightsQueryToolView,
      }))
    ),
  },
  logs_run_kql: {
    kind: "step",
    category: "metrics",
    view: lazy(() =>
      import("./views").then(({ LogsRunKqlToolView }) => ({
        default: LogsRunKqlToolView,
      }))
    ),
  },
} satisfies ToolUIRegistry;

/**
 * Static registry: the 16 contract tools plus the retired `feedback-insights`
 * camelCase names (ADR-047), kept so historical persisted chats still render a
 * first-class step card instead of the generic JSON fallback.
 */
export const devtoolsToolRegistry = {
  ...views,
  feedbackSummary: views.feedback_insights,
  feedbackList: views.feedback_search,
  feedbackCategorize: views.feedback_triage,
  uploadSummary: views.upload_summary,
  uploadList: views.upload_list,
} satisfies ToolUIRegistry;

/**
 * Tier 2 (dynamic) views. `devtools` is a progressive-disclosure capability, so
 * at runtime its tool parts are rewritten to the namespaced
 * `devtools_${toolName}` identity (ADR-057). Those keys are intentionally NOT
 * in `AllToolNames`, so they live only in the dynamic tier — reusing the same
 * lazy views so the rich cards render on the PD path too.
 */
export const devtoolsProgressiveDisclosureToolRegistry: Record<
  string,
  ToolUIMeta
> = Object.fromEntries(
  Object.entries(views).map(([name, meta]) => [`devtools_${name}`, meta])
);
